import React, { FormEvent, useReducer, useState } from 'react';

import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import SubmitBtn from '@components/buttons/FormButtons/SubmitBtn';
import { WarningBox } from '@components/cards/messageBox';
import Checkbox from '@components/inputs/Checkbox';
import Label from '@components/inputs/text/Label';
import WarningModal from '@components/modal/info/Warning';
import { NavBar } from '@components/navigation/NavBar/NavBar';
import Elliptical from '@components/spinners/Elliptical/Elliptical';
import { logger as sentryLogger } from '@sentry/react';
import { UploadParams } from '@services/documentStore/DocumentStore.types';
import DocumentStoreService from '@services/documentStore/DocumentStoreService';
import { IdentityDocumentType, IdentityVerificationDocument, ManualUploadRequest } from '@services/identityVerification/IdentityVerification.types';
import IdentityVerificationService from '@services/identityVerification/IdentityVerificationService';
import { Country, COUNTRY_NAMES } from '@utils/constants/localisation';
import { textBoxStyle } from '@utils/styles/textboxStyle';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import DocumentPanel from '../../components/panels/DocumentPanel';
import { DocumentDetail, documentDetailInitialState, documentDetailReducer } from './documentDetailReducer';
import { validateDocumentDetail } from './validateDocumentDetail';

const lenientNationalInsuranceNumberPattern = "^[A-Za-z]{2}(\\s*\\d){6}\\s*[A-Da-d]$"; // flexible pre-sanitisation
const strictNationalInsuranceNumberPattern = /^[A-Z]{2}[0-9]{6}[A-D]$/; // all caps & no spaces when submitting

/**
 * Validates identity verification documents using validateDocumentDetail from DocumentPanel.
 *
 * @param licenceDetails - Driver licence document details
 * @param secondIdDetails - Second ID document details (Passport, Medicare, Credit Card)
 * @param proofOfAddressDetails - Proof of address document details (Bank Statement, Utility Bill)
 * @param declarationChecked - Whether the declaration checkbox is checked
 * @param country - Country to resolve country-specific field requirements
 * @param nationalInsuranceNumber - Sanitised National Insurance Number (required for GB)
 * @returns Array of validation error messages (empty if valid)
 */
export const validateIdentityCheck = (
    licenceDetails: DocumentDetail,
    secondIdDetails: DocumentDetail,
    proofOfAddressDetails: DocumentDetail,
    declarationChecked: boolean,
    country: Country,
    nationalInsuranceNumber: string | null
): string[] => {
    const errors: string[] = [];

    // Validate each document panel using validateDocumentDetail
    errors.push(...validateDocumentDetail(licenceDetails, 'Driver Licence', country));
    errors.push(...validateDocumentDetail(secondIdDetails, 'Second ID', country));
    errors.push(...validateDocumentDetail(proofOfAddressDetails, 'Proof of Address', country));

    // Validate National Insurance Number for GB
    if (country === Country.GB) {
        if (!nationalInsuranceNumber) {
            errors.push('National Insurance Number is required');
        } else if (!strictNationalInsuranceNumberPattern.test(nationalInsuranceNumber)) {
            errors.push('National Insurance Number must be two letters, six numbers, ' +
                'and another letter A-D, e.g. QQ123456A');
        }
    }

    // Validate declaration checkbox
    if (!declarationChecked) {
        errors.push('Declaration must be checked');
    }

    return errors;
};

export default function IdentityVerificationDocumentUpload() {
    const { custId, country } = useParams() as { custId: string, country: Country };

    const navigate = useNavigate();

    const msalContext = useMsal();
    const isAuthenticated = useIsAuthenticated();

    const [declarationChecked, setDeclarationChecked] = useState(false);
    const [nationalInsuranceNumber, setNationalInsuranceNumber] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [wasSubmitted, setWasSubmitted] = useState(false);
    const [error, setError] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    const [licenceDetails, licenceDetailDispatch] = useReducer(
        documentDetailReducer,
        documentDetailInitialState
    );
    const [secondIdDetails, secondIdDetailDispatch] = useReducer(
        documentDetailReducer,
        documentDetailInitialState
    );
    const [proofOfAddressDetails, proofOfAddressDetailDispatch] = useReducer(
        documentDetailReducer,
        documentDetailInitialState
    );

    const handleSetNationalInsuranceNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Standardise to all caps with no spaces, e.g. "qq 12 34 56 a" -> "QQ123456A"
        const sanitisedNino = event.target.value.replace(/\s/g, "").toUpperCase();
        setNationalInsuranceNumber(sanitisedNino)
    }

    const handleSubmit = (event: FormEvent) => {
        setWasSubmitted(true)
        event.preventDefault();

        const errors = validateIdentityCheck(
            licenceDetails,
            secondIdDetails,
            proofOfAddressDetails,
            declarationChecked,
            country,
            nationalInsuranceNumber
        );
        setValidationErrors(errors);

        if (errors.length === 0) {
            setIsUploading(true);

            // Prepare list of documents and their files to upload
            const proofOfAddressWithCountry = { ...proofOfAddressDetails, country: COUNTRY_NAMES[country] };
            const allDocumentDetails = [licenceDetails, secondIdDetails, proofOfAddressWithCountry]
            const documents = allDocumentDetails.map(IdentityVerificationDocument.fromDocumentDetail)
            const prepRequest = new ManualUploadRequest(country, custId, documents, nationalInsuranceNumber);

            sentryLogger.info(`Preparing identity verification upload:`, { prepRequest });
            console.log('Preparing for manual upload:', prepRequest);
            IdentityVerificationService.prepareManualUpload(
                prepRequest,
                { msalContext, isAuthenticated }
            )
                .then(preparationResponse => {
                    sentryLogger.info(`Identity verification upload prepared with reference: ${preparationResponse.reference}, response: ${JSON.stringify(preparationResponse)}`);

                    const allUploads: UploadParams[] = [];
                    allDocumentDetails.forEach((documentDetail, docIndex) => {
                        const documentUrls = preparationResponse.presigned_upload_urls[docIndex];

                        if (documentDetail.frontFile) {
                            if (!documentUrls?.front_file) {
                                sentryLogger.error(`Missing front file URL in preparation response for document ${docIndex}, documentUrls: ${JSON.stringify(documentUrls)}`);
                                throw new Error('Missing upload URL for front file');
                            }
                            allUploads.push({
                                presignedUrl: documentUrls.front_file.presigned_upload_url,
                                file: documentDetail.frontFile,
                                contentType: documentDetail.frontFile.type
                            });
                        }
                        if (documentDetail.backFile) {
                            if (!documentUrls?.back_file) {
                                sentryLogger.error(`Missing back file URL in preparation response for document ${docIndex}, documentUrls: ${JSON.stringify(documentUrls)}`);
                                throw new Error('Missing upload URL for back file');
                            }
                            allUploads.push({
                                presignedUrl: documentUrls.back_file.presigned_upload_url,
                                file: documentDetail.backFile,
                                contentType: documentDetail.backFile.type
                            });
                        }
                    });

                    sentryLogger.info(`Starting upload of ${allUploads.length} files for reference: ${preparationResponse.reference}`);
                    return DocumentStoreService.uploadFiles(allUploads)
                        .then(uploadResults => {
                            const failedUploads = uploadResults.filter(result => !result.success);

                            if (failedUploads.length > 0) {
                                sentryLogger.error(`${failedUploads.length} file uploads failed for reference: ${preparationResponse.reference}, failures: ${JSON.stringify(failedUploads)}`);
                                console.error(`${failedUploads.length} uploads failed:`, failedUploads);
                                // TODO Call cleanup endpoint
                                setError(true);
                                setWasSubmitted(false);
                                setIsUploading(false);
                            } else {
                                sentryLogger.info(`Identity verification upload for reference ${preparationResponse.reference} completed successfully`);
                                console.log('All uploads completed successfully');
                                // Return to whichever view sent them here
                                navigate(-1);
                            }
                        });
                })
                .catch(error => {
                    sentryLogger.error(`Identity verification upload preparation failed: ${error}`);
                    console.error('Upload preparation failed:', error);
                    setError(true);
                    setWasSubmitted(false);
                    setIsUploading(false);
                });
        } else {
            setWasSubmitted(false);
        }
    };

    return (
        <>
            <NavBar pageTitle='Identity Details' />
            {isUploading &&
                <div
                    data-testid="loading-spinner"
                    style={{ display: 'flex', justifyContent: 'center', margin: '15vh' }}
                >
                    <Elliptical />
                </div>
            }
            {!isUploading &&
                <form style={{ margin: '2.5vh 2.5vw' }} onSubmit={handleSubmit}>
                    {
                        validationErrors.length > 0 &&
                        <WarningBox
                            testId='validation-errors'
                            message={
                                <>
                                    <div>Please fix the following errors before submitting:</div>
                                    <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                                        {validationErrors.map((error, index) => (
                                            <li key={index}>{error}</li>
                                        ))}
                                    </ul>
                                </>
                            }
                        />
                    }

                    <DocumentPanel
                        testId="driver-licence-panel"
                        title='Driver Licence'
                        availableDocumentTypes={[IdentityDocumentType.DRIVER_LICENCE]}
                        documentDetail={licenceDetails}
                        documentDetailDispatch={licenceDetailDispatch}
                        country={country}
                    />

                    <DocumentPanel
                        testId="second-id-panel"
                        title='Second Form of ID'
                        availableDocumentTypes={
                            country === Country.GB
                                ? [IdentityDocumentType.PCO]
                                : [
                                    IdentityDocumentType.PASSPORT,
                                    IdentityDocumentType.CREDIT_CARD,
                                    IdentityDocumentType.MEDICARE_CARD
                                ]
                        }
                        documentDetail={secondIdDetails}
                        documentDetailDispatch={secondIdDetailDispatch}
                        country={country}
                    />

                    <DocumentPanel
                        testId="proof-of-address-panel"
                        title='Proof of Address'
                        availableDocumentTypes={[
                            IdentityDocumentType.BANK_STATEMENT,
                            IdentityDocumentType.UTILITY_BILL
                        ]}
                        documentDetail={proofOfAddressDetails}
                        documentDetailDispatch={proofOfAddressDetailDispatch}
                        country={country}
                    />

                    {country === Country.GB && (
                        <div data-testid={"additional-info"}>
                            <div style={{ marginTop: '2vh', fontSize: '20px', fontWeight: 'bold' }}>
                                Additional Information
                            </div>
                            <div style={{ marginTop: '2vh' }}>
                                <Label text={"National Insurance Number"} requiredFieldInd />
                                <input
                                    type='text'
                                    name='nationalInsuranceNumber'
                                    data-testid='nino-input'
                                    required
                                    pattern={lenientNationalInsuranceNumberPattern}
                                    title="Two letters, six numbers, and another letter A-D, e.g. QQ123456A"
                                    style={textBoxStyle}
                                    onChange={handleSetNationalInsuranceNumber}
                                />
                            </div>
                        </div>
                    )}

                    <div data-testid="declaration-panel" style={{ margin: '2.5vh 0' }}>
                        <Checkbox
                            id="declaration"
                            label={`I, ${msalContext.accounts.at(0)?.name}, ` +
                                `declare that the customer identity has been verified by me.`}
                            required
                            onChange={setDeclarationChecked}
                        />
                    </div>

                    <div style={{ margin: '2.5vh 0' }}>
                        <SubmitBtn
                            text='Submit'
                            testId='submit-button'
                            disabled={wasSubmitted}
                        />
                    </div>
                </form>
            }
            {
                error &&
                <WarningModal
                    message='Something went wrong'
                    additionalInfo='We’re sorry, we were unable to process your request. Please try again later and if the issue persists, please log a ticket with IT Helpdesk.'
                    onClickClose={(event: any) => {
                        event.preventDefault();
                        setError(false);
                        window.location.reload();
                    }} />
            }
        </>
    )
}
